import React from 'react';

import { WarningTwoTone } from '@ant-design/icons';
import { theme as antTheme, Form, Input, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { ResizeCallbackData } from 'react-resizable';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { NSKeys } from '@app/i18n';
import { RetrieveWarehouseSkuSchema } from '@app/models/wms';
import { WAREHOUSE_NOMENCLATURE_TABLE_COLUMNS } from '@app/services/appSettings';
import { formatDateAndTimeShort } from '@app/services/date';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { useTableParams } from '@app/services/hooks/useTableParams';
import { selectIsCounterpartySpace } from '@app/store/currentSessionReducer';
import { TableCellEllipsisText } from '@components/TableCellEllipsisText';
import { TableDnDResizable } from '@components/TableDnDResizable';
import { TABS } from '@pages/Warehouses/pages/Single/interfaces';

import { useGetWarehouseSkusQuery } from '../../../../hooks';

export const Nomenclature: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = antTheme.useToken();
  const warehousesT = useTranslation(NSKeys.warehouses);
  const commonT = useTranslation(NSKeys.common);
  const isCounterparty = useLocalSelector(selectIsCounterpartySpace);
  const { current, pageSize, setPagination } = useTableParams();
  const searchValue = searchParams.get('q') || '';
  const [searchInputValue, setSearchInputValue] = React.useState(searchValue);

  const { data, isLoading } = useGetWarehouseSkusQuery({
    warehouse_uid: params.id || '',
    offset: (current - 1) * pageSize,
    limit: pageSize,
    q: searchValue || '',
  });

  React.useEffect(() => {
    setSearchInputValue(searchValue);
  }, [searchValue]);

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set('q', value);
    } else {
      params.delete('q');
    }

    params.set('current', '1');
    setSearchParams(params);
  };

  const renderProductLink = React.useCallback(
    (productUid?: string | null, label?: string | null) => {
      if (!productUid || isCounterparty) {
        return <TableCellEllipsisText value={label} />;
      }

      return (
        <Typography.Link
          ellipsis
          style={{ display: 'block', width: '100%' }}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/dashboard/products/${productUid}`);
          }}>
          {label || productUid}
        </Typography.Link>
      );
    },
    [navigate, isCounterparty]
  );

  const columns = React.useMemo<ColumnsType<RetrieveWarehouseSkuSchema>>(
    () => [
      {
        title: warehousesT.t('tableColumns.title'),
        dataIndex: 'title',
        key: 'title',
        width: 260,
        ellipsis: true,
        render: (_: RetrieveWarehouseSkuSchema['title'], record) => renderProductLink(record.product_uid, record.title),
      },
      {
        title: warehousesT.t('tableColumns.code'),
        dataIndex: 'code',
        key: 'code',
        width: 160,
        ellipsis: true,
        render: (value: RetrieveWarehouseSkuSchema['code']) => <TableCellEllipsisText value={value} />,
      },
      {
        title: warehousesT.t('tableColumns.ref_id'),
        dataIndex: 'ref_id',
        key: 'ref_id',
        width: 220,
        ellipsis: true,
        render: (value: string) => <TableCellEllipsisText value={value} />,
      },
      {
        title: warehousesT.t('tableColumns.dt_updated'),
        dataIndex: 'dt_updated',
        key: 'dt_updated',
        width: 180,
        ellipsis: true,
        render: (value: string) => <TableCellEllipsisText value={formatDateAndTimeShort(value)} />,
      },
      // {
      //   title: warehousesT.t('tableColumns.product_uid'),
      //   dataIndex: 'product_uid',
      //   key: 'product_uid',
      //   width: 220,
      //   ellipsis: true,
      //   render: (value: RetrieveWarehouseSkuSchema['product_uid']) => renderProductLink(value, value),
      // },
      {
        title: warehousesT.t('tableColumns.available_stock'),
        dataIndex: 'available_stock',
        key: 'available_stock',
        width: 140,
        ellipsis: true,
        render: (value: number) => <TableCellEllipsisText value={String(value ?? '-')} />,
      },
      {
        title: warehousesT.t('tableColumns.import_error_code'),
        dataIndex: 'import_error_code',
        key: 'import_error_code',
        width: 140,
        align: 'center',
        render: (value: RetrieveWarehouseSkuSchema['import_error_code'], record) =>
          value ? (
            <Typography.Link
              onClick={(event) => {
                event.stopPropagation();
                const nextParams = new URLSearchParams();
                if (record.code) {
                  nextParams.set('code', record.code);
                }
                navigate(`/dashboard/warehouses/${params.id}/${TABS.IMPORT_ERRORS}?${nextParams.toString()}`);
              }}>
              <WarningTwoTone twoToneColor={token.colorError} style={{ fontSize: 18 }} />
            </Typography.Link>
          ) : null,
      },
    ],
    [params.id, renderProductLink, token.colorError, warehousesT]
  );

  const storageKey = `${params.id || 'warehouse'}${WAREHOUSE_NOMENCLATURE_TABLE_COLUMNS}`;
  const [localStorageColumns, setLocalStorageColumns] = useLocalStorage(
    storageKey,
    columns.map(({ render, ...rest }) => rest)
  );
  const totalTableWidth = React.useMemo(
    () => localStorageColumns.reduce((sum, column) => sum + Number(column.width || 0), 0),
    [localStorageColumns]
  );

  const handleResize =
    (index: number) =>
    (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
      const newColumns = [...localStorageColumns];
      newColumns[index] = {
        ...newColumns[index],
        width: size.width > 20 ? size.width : 20,
      };
      setLocalStorageColumns(newColumns);
    };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}>
        <Form.Item style={{ marginBottom: 0 }}>
          <Input.Search
            style={{ width: 280 }}
            size="middle"
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            placeholder={commonT.t('search')}
            enterButton
            disabled={isLoading}
            onSearch={handleSearch}
          />
        </Form.Item>
      </div>
      <TableDnDResizable<RetrieveWarehouseSkuSchema>
        localStorageName={storageKey}
        dataSource={data?.items}
        rowKey="uid"
        className="tableClassname"
        columns={localStorageColumns.map((column, index) => ({
          ...column,
          onHeaderCell: (column: any) => ({
            width: column.width,
            onResize: handleResize(index) as React.ReactEventHandler<any>,
          }),
          render: columns.find((baseColumn) => baseColumn.key === column.key)?.render,
        }))}
        onColumnsChange={setLocalStorageColumns}
        loading={isLoading}
        tableLayout="fixed"
        scroll={{ x: totalTableWidth }}
        pagination={{
          current,
          pageSize,
          total: data?.total,
          showQuickJumper: true,
          showSizeChanger: true,
          position: ['bottomCenter'],
          onChange: (newPage, newSize) => {
            setPagination(newPage, newSize);
          },
          showTotal: (total, range) => `${range[0]}-${range[1]} ${commonT.t('paginationOf')} ${total}`,
        }}
      />
    </>
  );
};
